/**
 * The SFX bank and its progressive pre-render (audio-direction §7): every sound's variations are rendered
 * offline in priority order (`render.order`), a few at a time, from idle callbacks, so boot and play never wait
 * on it. Sounds become playable one by one (`takes[i]` stays null until then; the engine skips them). No user
 * gesture is needed: `OfflineAudioContext` renders without one, so this starts during boot.
 */
import { logger } from '../../core/logger'
import { renderOrder, SOUND_IDS, soundDefs, type SfxConfig, type SoundDef, type SoundId } from '../sfxSchema'
import { renderSound, type OfflineFactory } from './render'

export interface SfxBankStats {
  /** `performance.now()` at the first and the last render (ms); -1 until then. */
  startedAt: number
  finishedAt: number
  /** Main-thread time spent building graphs and post-processing (ms). */
  mainThreadMs: number
  /** Wall time per sound from its start to its buffers (ms), by sound index. */
  readonly perSoundMs: Float64Array
}

export interface SfxBank {
  readonly ids: readonly SoundId[]
  /** Defs by sound index; shared with the config, so live mix tuning applies. */
  readonly defs: readonly SoundDef[]
  /** Rendered variations by sound index; null until that sound is rendered. */
  readonly takes: (readonly AudioBuffer[] | null)[]
  readonly sampleRate: number
  /** Sounds rendered so far. */
  ready: number
  readonly stats: SfxBankStats
}

export function createSfxBank(config: SfxConfig): SfxBank {
  return {
    ids: SOUND_IDS,
    defs: soundDefs(config),
    takes: SOUND_IDS.map(() => null),
    sampleRate: config.render.sampleRate,
    ready: 0,
    stats: { startedAt: -1, finishedAt: -1, mainThreadMs: 0, perSoundMs: new Float64Array(SOUND_IDS.length) },
  }
}

/** Runs `cb` when the main thread is idle (requestIdleCallback, else a macrotask). */
export type IdleScheduler = (cb: () => void) => void

export function browserIdleScheduler(): IdleScheduler {
  if (typeof requestIdleCallback === 'function') return (cb) => void requestIdleCallback(() => cb(), { timeout: 60 })
  return (cb) => void setTimeout(cb, 0)
}

export interface Prerender {
  readonly bank: SfxBank
  /** Resolves when every sound is rendered (never rejects: failures are logged and the sound stays silent). */
  readonly done: Promise<void>
  /** Re-renders one sound now (after tuning it; the audition page). */
  rerender(sound: number): Promise<void>
}

export interface PrerenderOptions {
  factory: OfflineFactory
  schedule: IdleScheduler
  /** Plain buffer creation for loop folding (`(c, n, r) => new AudioBuffer({...})` in the browser). */
  createBuffer: (channels: number, length: number, rate: number) => AudioBuffer
  now?: () => number
}

/** Starts rendering the whole bank progressively, priority sounds first. */
export function startPrerender(bank: SfxBank, config: SfxConfig, opts: PrerenderOptions): Prerender {
  const now = opts.now ?? ((): number => performance.now())
  const queue = renderOrder(config)
  const r = config.render
  let active = 0
  let resolveDone: () => void = () => {}
  const done = new Promise<void>((resolve) => {
    resolveDone = resolve
  })

  function renderOne(sound: number): Promise<void> {
    const t0 = now()
    if (bank.stats.startedAt < 0) bank.stats.startedAt = t0
    const job = renderSound(opts.factory, bank.defs[sound], sound, r.seed, r.sampleRate, r.tailFadeSeconds, opts.createBuffer)
    bank.stats.mainThreadMs += now() - t0
    return job.then(
      (rendered) => {
        const t1 = now()
        const first = bank.takes[sound] === null
        bank.takes[sound] = rendered.takes
        if (first) bank.ready++
        bank.stats.perSoundMs[sound] = t1 - t0
        bank.stats.finishedAt = t1
      },
      (err: unknown) => logger.warn(`audio: rendering ${SOUND_IDS[sound]} failed; it stays silent`, err),
    )
  }

  function pump(): void {
    while (active < r.concurrency && queue.length > 0) {
      const sound = queue.shift() as number
      active++
      void renderOne(sound).then(() => {
        active--
        if (queue.length > 0) opts.schedule(pump)
        else if (active === 0) {
          const s = bank.stats
          logger.info(`audio: ${bank.ready} sounds pre-rendered in ${(s.finishedAt - s.startedAt).toFixed(0)} ms (main thread ${s.mainThreadMs.toFixed(1)} ms)`)
          resolveDone()
        }
      })
    }
  }

  opts.schedule(pump)
  return { bank, done, rerender: renderOne }
}
